import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalSlice } from "app/slice";
import { toast } from "app/components/ui/use-toast";
import { getActivationFlowData } from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";
import {
  persistPreloadedSimChoosePlanTierFrequencyOnly,
  persistPreloadedSimUpgradeCardDraft,
  persistSimCardUpsellDataAndFrequencySelections,
  persistSimCardUpsellKeepIncludedAllocation,
} from "../utils/sim-card-upsell-plan-persistence";
import {
  SIM_CARD_UPSELL_FREQUENCY_OPTIONS,
  SIM_CARD_UPSELL_PRIMARY_DATA_OPTIONS,
} from "../utils/sim-card-upsell-plan-options";
import {
  buildFrequencyOptionsForPlan,
  computeOriginalPriceCompact,
  findUpgradePlanByNameAndFrequency,
  buildSingleNetworkFeatureLines,
  formatUsdCompact,
  frequencyDisplayLabel,
  frequencyKeyFromMonths,
  frequencySuffixFromKey,
  normalizePrimaryDataTierKey,
  parseCurrencyToNumber,
} from "../utils/preloaded-sim-plan-mappers";
import {
  INCLUDED_PRIMARY_DATA_FEATURES,
  upgradeCardTitle,
} from "../utils/choose-service-plan-utils";
import { resolveIncludedSecurityPlanTierFromSimData } from "../utils/included-security-plan-utils";
import {
  buildPreloadedSimFamilyCompleteSecurityPlansRequestParams,
  commitPreloadedSimFamilyCompleteSecurityPlanId,
  commitPreloadedSimIncludedSecuritySelection,
  getPreloadedSimCheckoutNavigateState,
  pickCompleteFamilySecurityPlanIdFromInventoryResponse,
  preloadedSimFamilyCompleteNeedsSecurityPlanApiForCheckout,
  resolvePreloadedSimUpsellStepSkips,
} from "../utils/preloaded-sim-upsell-step-skips";

export function usePreloadedSimChooseServicePlan() {
  const navigate = useNavigate();
  const { useGetMobileSimInventoryPlansQuery, useLazyGetMobileSimInventorySecurityPlansQuery } =
    useGlobalSlice();
  const [fetchSecurityPlansForCheckout] =
    useLazyGetMobileSimInventorySecurityPlansQuery();
  const {
    data: plansResponse,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetMobileSimInventoryPlansQuery(undefined);
  const upgradePlans = useMemo(() => {
    const rows = plansResponse?.plans;
    return Array.isArray(rows) ? rows : [];
  }, [plansResponse]);

  const simData = getActivationFlowData()?.simData;
  const includedPlan = useMemo(() => {
    if (!simData || typeof simData !== "object") return null;
    const data = simData as Record<string, unknown>;
    const included = data.planIncluded;
    if (!included || typeof included !== "object") return null;
    return included as Record<string, unknown>;
  }, [simData]);

  const includedTierKey = normalizePrimaryDataTierKey(includedPlan?.name);
  const includedFrequencyKey = frequencyKeyFromMonths(includedPlan?.frequency);
  const frequencyRank: Record<string, number> = useMemo(
    () =>
      SIM_CARD_UPSELL_FREQUENCY_OPTIONS.reduce(
        (acc, row, index) => ({ ...acc, [row.value]: index }),
        {} as Record<string, number>,
      ),
    [],
  );
  const includedFrequencyRank = frequencyRank[includedFrequencyKey] ?? 0;

  const orderedTierKeys = useMemo(
    () => SIM_CARD_UPSELL_PRIMARY_DATA_OPTIONS.map((row) => row.value),
    [],
  );
  const tierRank = useMemo(
    () =>
      orderedTierKeys.reduce(
        (acc, key, index) => ({ ...acc, [key]: index }),
        {} as Record<string, number>,
      ),
    [orderedTierKeys],
  );
  const includedTierRank = tierRank[includedTierKey] ?? -1;

  /** No higher cadence to sell when already Unlimited @ 12 mo — hide the upgrade card. */
  const showUpgradeToCard = !(
    includedTierKey === "unlimited" && includedFrequencyKey === "twelveMonths"
  );

  const [primaryDataTierKey, setPrimaryDataTierKey] = useState(() => {
    const act = getActivationFlowData();
    const draft = act?.preloadedSimUpgradeCardDraft;
    if (
      draft?.primaryDataTier &&
      SIM_CARD_UPSELL_PRIMARY_DATA_OPTIONS.some(
        (o) => o.value === draft.primaryDataTier,
      )
    ) {
      return draft.primaryDataTier;
    }
    if (
      act?.preloadedSimOptedForDataOrFrequencyUpsell &&
      act.preloadedSimPrimaryDataTier &&
      SIM_CARD_UPSELL_PRIMARY_DATA_OPTIONS.some(
        (o) => o.value === act.preloadedSimPrimaryDataTier,
      )
    ) {
      return act.preloadedSimPrimaryDataTier;
    }
    return "unlimited";
  });

  const [serviceFrequencyKey, setServiceFrequencyKey] = useState(() => {
    const act = getActivationFlowData();
    const draft = act?.preloadedSimUpgradeCardDraft;
    const sim = act?.simData;
    const includedFk = frequencyKeyFromMonths(
      ((sim as Record<string, unknown> | undefined)?.planIncluded as
        | Record<string, unknown>
        | undefined)?.frequency,
    );
    if (
      draft?.serviceFrequency &&
      SIM_CARD_UPSELL_FREQUENCY_OPTIONS.some(
        (o) => o.value === draft.serviceFrequency,
      )
    ) {
      return draft.serviceFrequency;
    }
    if (
      act?.preloadedSimOptedForDataOrFrequencyUpsell &&
      act.preloadedSimServiceFrequency &&
      SIM_CARD_UPSELL_FREQUENCY_OPTIONS.some(
        (o) => o.value === act.preloadedSimServiceFrequency,
      )
    ) {
      return act.preloadedSimServiceFrequency;
    }
    return includedFk;
  });
  const suppressLockInNavUntilRef = useRef(0);
  const lockInCheckoutNavigateBusyRef = useRef(false);

  useEffect(() => {
    if (!showUpgradeToCard) {
      setPrimaryDataTierKey(includedTierKey || "1gb");
      setServiceFrequencyKey(includedFrequencyKey);
    }
  }, [showUpgradeToCard, includedTierKey, includedFrequencyKey]);

  const availablePlanNamesOrdered = useMemo(() => {
    const names = Array.from(
      new Set(
        upgradePlans
          .map((plan) => (typeof plan?.name === "string" ? plan.name : ""))
          .filter(Boolean),
      ),
    );
    return names.sort((a, b) => {
      const aKey = normalizePrimaryDataTierKey(a);
      const bKey = normalizePrimaryDataTierKey(b);
      return (
        (tierRank[aKey] ?? Number.MAX_SAFE_INTEGER) -
        (tierRank[bKey] ?? Number.MAX_SAFE_INTEGER)
      );
    });
  }, [tierRank, upgradePlans]);

  const apiPlanNameByKey = useMemo(() => {
    const out: Record<string, string> = {};
    availablePlanNamesOrdered.forEach((name) => {
      out[normalizePrimaryDataTierKey(name)] = name;
    });
    return out;
  }, [availablePlanNamesOrdered]);

  const planSelectOptions = useMemo(
    () =>
      SIM_CARD_UPSELL_PRIMARY_DATA_OPTIONS.map((row) => {
        const key = row.value;
        const rank = tierRank[key] ?? -1;
        const apiName = apiPlanNameByKey[key];
        const existsInApi = Boolean(apiName);
        let disabled = true;
        if (existsInApi) {
          const planFrequencies = buildFrequencyOptionsForPlan(apiName, upgradePlans);
          const selectedFrequencyRank = frequencyRank[serviceFrequencyKey] ?? -1;
          const selectedFrequencyExistsForPlan = planFrequencies.some(
            (opt) => opt.key === serviceFrequencyKey,
          );
          const isLowerFrequencyThanIncluded =
            selectedFrequencyRank < includedFrequencyRank;
          const isSameFrequencyAsIncluded =
            serviceFrequencyKey === includedFrequencyKey;
          const isHigherFrequencyThanIncluded =
            selectedFrequencyRank > includedFrequencyRank;

          if (isLowerFrequencyThanIncluded) {
            disabled = true;
          } else if (isHigherFrequencyThanIncluded) {
            disabled = !selectedFrequencyExistsForPlan;
          } else if (isSameFrequencyAsIncluded) {
            disabled = rank <= includedTierRank;
          } else {
            disabled = true;
          }
        }
        return {
          key,
          label: row.label,
          apiName: apiName || row.label,
          disabled,
        };
      }),
    [
      apiPlanNameByKey,
      frequencyRank,
      includedFrequencyRank,
      includedFrequencyKey,
      includedTierKey,
      includedTierRank,
      serviceFrequencyKey,
      tierRank,
      upgradePlans,
    ],
  );
  const showPlanSelect = planSelectOptions.some((opt) => !opt.disabled);

  useEffect(() => {
    if (!showUpgradeToCard) return;
    const enabled = planSelectOptions.filter((opt) => !opt.disabled);
    if (!enabled.length) return;
    const selected = planSelectOptions.find((opt) => opt.key === primaryDataTierKey);
    if (!selected || selected.disabled) {
      const unlimited = enabled.find((opt) => opt.key === "unlimited");
      const defaultPlanKey = unlimited?.key ?? enabled[0].key;
      if (primaryDataTierKey !== defaultPlanKey) setPrimaryDataTierKey(defaultPlanKey);
    }
  }, [planSelectOptions, primaryDataTierKey, showUpgradeToCard]);

  const selectedPlanName = useMemo(
    () => planSelectOptions.find((opt) => opt.key === primaryDataTierKey)?.apiName || "",
    [planSelectOptions, primaryDataTierKey],
  );
  const selectedPlanKey = normalizePrimaryDataTierKey(primaryDataTierKey);

  const availableFrequencySetForSelectedPlan = useMemo(() => {
    const all = buildFrequencyOptionsForPlan(selectedPlanName, upgradePlans);
    return new Set(all.map((opt) => opt.key));
  }, [selectedPlanName, upgradePlans]);

  const frequencyOptions = useMemo(() => {
    return SIM_CARD_UPSELL_FREQUENCY_OPTIONS.map((opt) => {
      const key = opt.value;
      const existsForPlan = availableFrequencySetForSelectedPlan.has(key as any);
      const planForFrequency = findUpgradePlanByNameAndFrequency(
        upgradePlans,
        selectedPlanName,
        key,
      );
      const savingsNumber = parseCurrencyToNumber(planForFrequency?.savings);
      const planPriceNumber = parseCurrencyToNumber(planForFrequency?.price);
      const originalPrice =
        planPriceNumber != null && savingsNumber != null
          ? planPriceNumber + savingsNumber
          : null;
      const savingsPercent =
        originalPrice && originalPrice > 0 && savingsNumber && savingsNumber > 0
          ? Math.round((savingsNumber / originalPrice) * 100)
          : null;
      const savingsLabel =
        savingsPercent && savingsPercent > 0
          ? ` - Save ${savingsPercent}%`
          : "";
      const rank = frequencyRank[key] ?? -1;
      const isUnlimitedIncluded = includedTierKey === "unlimited";
      const isDowngradeFrequency =
        !isUnlimitedIncluded && rank < includedFrequencyRank;
      const disableFrequencyForUnlimitedIncluded =
        isUnlimitedIncluded && rank <= includedFrequencyRank;
      return {
        key,
        label: `${frequencyDisplayLabel(key)}${savingsLabel}`,
        disabled:
          !existsForPlan ||
          isDowngradeFrequency ||
          disableFrequencyForUnlimitedIncluded,
      };
    });
  }, [
    availableFrequencySetForSelectedPlan,
    frequencyRank,
    includedFrequencyRank,
    includedTierKey,
    selectedPlanName,
    upgradePlans,
  ]);
  const showFrequencySelect = frequencyOptions.some((opt) => !opt.disabled);
  const canUpgradeSelection = Boolean(selectedPlanName) && showFrequencySelect;

  const billingSuffixForUpgradeTitle = serviceFrequencyKey;

  const frequencyLabelMap = useMemo(
    () => frequencyOptions.reduce(
      (acc, row) => ({ ...acc, [row.key]: row.label }),
      {} as Record<string, string>,
    ),
    [frequencyOptions],
  );

  useEffect(() => {
    if (!showUpgradeToCard) return;
    const enabled = frequencyOptions.filter((opt) => !opt.disabled);
    if (!enabled.length) return;
    const shouldIncrementFromCurrent =
      includedTierKey === "unlimited" && selectedPlanKey === "unlimited";
    const next = shouldIncrementFromCurrent
      ? enabled.find((opt) => (frequencyRank[opt.key] ?? -1) > includedFrequencyRank) ??
        enabled[0]
      : enabled.find((opt) => opt.key === includedFrequencyKey) ?? enabled[0];
    if (!enabled.some((opt) => opt.key === serviceFrequencyKey)) {
      setServiceFrequencyKey(next.key);
    }
  }, [
    frequencyOptions,
    frequencyRank,
    includedFrequencyKey,
    includedFrequencyRank,
    includedTierKey,
    selectedPlanKey,
    serviceFrequencyKey,
    showUpgradeToCard,
  ]);

  useEffect(() => {
    if (!showUpgradeToCard) return;
    const selectedPlan = planSelectOptions.find((opt) => opt.key === primaryDataTierKey);
    if (selectedPlan?.disabled) {
      const enabled = planSelectOptions.filter((opt) => !opt.disabled);
      if (enabled[0]) setPrimaryDataTierKey(enabled[0].key);
    }
  }, [planSelectOptions, primaryDataTierKey, showUpgradeToCard]);

  useEffect(() => {
    if (!showUpgradeToCard) return;
    const selectedFrequency = frequencyOptions.find(
      (opt) => opt.key === serviceFrequencyKey,
    );
    if (selectedFrequency?.disabled) {
      const enabled = frequencyOptions.filter((opt) => !opt.disabled);
      if (enabled[0]) setServiceFrequencyKey(enabled[0].key);
    }
  }, [frequencyOptions, serviceFrequencyKey, showUpgradeToCard]);

  const selectedPlanDisabled = useMemo(() => {
    const selected = planSelectOptions.find((opt) => opt.key === primaryDataTierKey);
    return selected ? selected.disabled : true;
  }, [planSelectOptions, primaryDataTierKey]);
  const selectedFrequencyDisabled = useMemo(() => {
    const selected = frequencyOptions.find((opt) => opt.key === serviceFrequencyKey);
    return selected ? selected.disabled : true;
  }, [frequencyOptions, serviceFrequencyKey]);
  const canLockInSelection =
    canUpgradeSelection && !selectedPlanDisabled && !selectedFrequencyDisabled;

  const selectedUpgradePlan = useMemo(
    () =>
      findUpgradePlanByNameAndFrequency(
        upgradePlans,
        selectedPlanName,
        serviceFrequencyKey,
      ),
    [upgradePlans, selectedPlanName, serviceFrequencyKey],
  );

  const includedPriceNumber = parseCurrencyToNumber(includedPlan?.price) ?? 0;
  const includedPrice = includedPriceNumber > 0 ? formatUsdCompact(includedPriceNumber) : "$0";
  const includedOriginalPrice = computeOriginalPriceCompact(
    includedPlan?.price,
    includedPlan?.savings,
  );

  const auraProtectionFooterLine = useMemo(() => {
    const tier = resolveIncludedSecurityPlanTierFromSimData(simData);
    return tier === "completeFamily"
      ? "Aura's Complete Family Protection"
      : "Aura's Complete Individual Protection";
  }, [simData]);

  const includedFeatures = useMemo(() => {
    const fromApi = buildSingleNetworkFeatureLines(includedPlan?.features);
    const base =
      fromApi.length > 0 ? fromApi : [...INCLUDED_PRIMARY_DATA_FEATURES];
    return [...base, auraProtectionFooterLine];
  }, [includedPlan, auraProtectionFooterLine]);

  const dataAndFrequencyUpsellFeatures = useMemo(
    () => {
      const mappedPlanFeatures = buildSingleNetworkFeatureLines(
        selectedUpgradePlan?.features,
      );
      const renewsLine = (() => {
        const total = parseCurrencyToNumber(selectedUpgradePlan?.price);
        if (!total || total <= 0) return "Renews at your selected billing cadence";
        const periods: Record<string, number> = {
          monthly: 1,
          threeMonths: 3,
          sixMonths: 6,
          twelveMonths: 12,
        };
        const months = periods[serviceFrequencyKey] || 1;
        const monthly = total / months;
        const monthlyText = `${formatUsdCompact(monthly)}/mo`;
        if (months === 1) return `Renews at ${monthlyText}`;
        return `Renews at ${monthlyText} (${formatUsdCompact(total)} every ${months} months)`;
      })();
      return [...mappedPlanFeatures, auraProtectionFooterLine, renewsLine];
    },
    [serviceFrequencyKey, selectedUpgradePlan, auraProtectionFooterLine],
  );

  const isRequestPending = isLoading || isFetching;
  const isSkeleton = isRequestPending;

  useEffect(() => {
    if (!simData || isRequestPending || isError || !showUpgradeToCard) return;
    const handle = window.setTimeout(() => {
      persistPreloadedSimUpgradeCardDraft(primaryDataTierKey, serviceFrequencyKey);
    }, 350);
    return () => window.clearTimeout(handle);
  }, [
    simData,
    isRequestPending,
    isError,
    showUpgradeToCard,
    primaryDataTierKey,
    serviceFrequencyKey,
  ]);

  const showPlanSelectSkeleton = isRequestPending
    ? Boolean(includedPlan)
    : showPlanSelect;
  const showFrequencySelectSkeleton = isRequestPending
    ? Boolean(includedPlan)
    : showFrequencySelect;
  const plansData: unknown[] | undefined = upgradePlans;
  const showEmptyPlans =
    !isRequestPending &&
    !isError &&
    plansData !== undefined &&
    plansData.length === 0;

  useEffect(() => {
    if (!simData || isRequestPending || isError) return;
    const handle = window.setTimeout(() => {
      const act = getActivationFlowData();
      const includedIdRaw = (includedPlan as { _id?: unknown } | null)?._id;
      const includedId =
        typeof includedIdRaw === "string" ? includedIdRaw.trim() : "";
      const sessionCommittedIncluded =
        act?.preloadedSimOptedForDataOrFrequencyUpsell === false &&
        includedId &&
        String(act?.preloadedSimPlanId || "").trim() === includedId &&
        act?.preloadedSimPrimaryDataTier === includedTierKey &&
        act?.preloadedSimServiceFrequency === includedFrequencyKey;
      /** Do not let draft/explore UI overwrite a committed included allocation (e.g. after Keep Current + history navigation). */
      if (sessionCommittedIncluded) {
        return;
      }
      if (!showUpgradeToCard) {
        const sameAsIncludedBaseAllocation =
          primaryDataTierKey === includedTierKey &&
          serviceFrequencyKey === includedFrequencyKey;
        if (sameAsIncludedBaseAllocation) {
          persistSimCardUpsellDataAndFrequencySelections(
            includedTierKey || "1gb",
            includedFrequencyKey,
            (includedPlan as any)?._id,
            false,
          );
        }
        return;
      }
      const sameAsIncludedBaseAllocation =
        primaryDataTierKey === includedTierKey &&
        serviceFrequencyKey === includedFrequencyKey;
      if (sameAsIncludedBaseAllocation) {
        persistSimCardUpsellDataAndFrequencySelections(
          includedTierKey || "1gb",
          includedFrequencyKey,
          (includedPlan as any)?._id,
          false,
        );
        return;
      }
      if (canLockInSelection && selectedUpgradePlan) {
        persistSimCardUpsellDataAndFrequencySelections(
          primaryDataTierKey,
          serviceFrequencyKey,
          (selectedUpgradePlan as any)?._id,
          true,
        );
        return;
      }
      persistPreloadedSimChoosePlanTierFrequencyOnly(
        primaryDataTierKey,
        serviceFrequencyKey,
      );
    }, 350);
    return () => window.clearTimeout(handle);
  }, [
    simData,
    isRequestPending,
    isError,
    showUpgradeToCard,
    primaryDataTierKey,
    serviceFrequencyKey,
    includedTierKey,
    includedFrequencyKey,
    includedPlan,
    canLockInSelection,
    selectedUpgradePlan,
  ]);

  useEffect(() => {
    const zipCode = getActivationFlowData()?.zipCode;
    if (!zipCode) {
      navigate("/activation", { replace: true });
    }
  }, [navigate]);

  const goKeepIncludedAllocation = useCallback(() => {
    /** Same as activation footer: commit included plan without overwriting upgrade-card draft. */
    persistSimCardUpsellKeepIncludedAllocation();
    const skips = resolvePreloadedSimUpsellStepSkips(
      getActivationFlowData()?.simData,
    );
    if (skips.skipSecurityPlan) {
      commitPreloadedSimIncludedSecuritySelection();
      navigate("/checkout", getPreloadedSimCheckoutNavigateState());
      return;
    }
    navigate("/activation/preloaded-sim/security-plan-upgrade");
  }, [navigate]);

  const handleUpsellSelectOpenChange = useCallback((open: boolean) => {
    if (!open) {
      suppressLockInNavUntilRef.current = Date.now() + 200;
    }
  }, []);

  const goLockInDataAndFrequencyUpsell = useCallback(async () => {
    if (!canLockInSelection) return;
    if (Date.now() < suppressLockInNavUntilRef.current) {
      return;
    }
    if (lockInCheckoutNavigateBusyRef.current) return;
    persistSimCardUpsellDataAndFrequencySelections(
      primaryDataTierKey,
      serviceFrequencyKey,
      (selectedUpgradePlan as any)?._id,
      true,
    );
    const simData = getActivationFlowData()?.simData;
    const skips = resolvePreloadedSimUpsellStepSkips(simData);
    if (skips.skipSecurityPlan) {
      if (preloadedSimFamilyCompleteNeedsSecurityPlanApiForCheckout(simData, serviceFrequencyKey)) {
        lockInCheckoutNavigateBusyRef.current = true;
        try {
          const params = buildPreloadedSimFamilyCompleteSecurityPlansRequestParams(
            serviceFrequencyKey,
          );
          const sec = await fetchSecurityPlansForCheckout(params).unwrap();
          const planId = pickCompleteFamilySecurityPlanIdFromInventoryResponse(sec);
          if (!planId) {
            toast({
              title: "Security plan",
              description:
                "We could not load your Family Complete plan for the selected billing period. Please try again.",
              variant: "error",
            });
            return;
          }
          commitPreloadedSimFamilyCompleteSecurityPlanId(planId);
        } catch (error: unknown) {
          const err = error as { data?: { message?: string }; message?: string };
          const description =
            (typeof err?.data?.message === "string" && err.data.message) ||
            (typeof err?.message === "string" && err.message) ||
            "We could not load security plans. Please try again.";
          toast({
            title: "Security plan",
            description,
            variant: "error",
          });
          return;
        } finally {
          lockInCheckoutNavigateBusyRef.current = false;
        }
      } else {
        commitPreloadedSimIncludedSecuritySelection();
      }
      navigate("/checkout", getPreloadedSimCheckoutNavigateState());
      return;
    }
    navigate("/activation/preloaded-sim/security-plan-upgrade");
  }, [
    canLockInSelection,
    fetchSecurityPlansForCheckout,
    navigate,
    primaryDataTierKey,
    serviceFrequencyKey,
    selectedUpgradePlan,
  ]);

  return {
    INCLUDED_PRIMARY_DATA_FEATURES,
    includedPlan,
    includedTierKey,
    includedFrequencyKey,
    frequencyRank,
    includedFrequencyRank,
    orderedTierKeys,
    tierRank,
    includedTierRank,
    primaryDataTierKey,
    setPrimaryDataTierKey,
    serviceFrequencyKey,
    setServiceFrequencyKey,
    availablePlanNamesOrdered,
    apiPlanNameByKey,
    planSelectOptions,
    showPlanSelect,
    selectedPlanName,
    selectedPlanKey,
    availableFrequencySetForSelectedPlan,
    frequencyOptions,
    showFrequencySelect,
    showPlanSelectSkeleton,
    showFrequencySelectSkeleton,
    canUpgradeSelection,
    billingSuffixForUpgradeTitle,
    frequencyLabelMap,
    selectedPlanDisabled,
    selectedFrequencyDisabled,
    canLockInSelection,
    selectedUpgradePlan,
    includedPriceNumber,
    includedPrice,
    includedOriginalPrice,
    includedFeatures,
    dataAndFrequencyUpsellFeatures,
    isRequestPending,
    isSkeleton,
    plansData,
    showEmptyPlans,
    isError,
    error,
    refetch,
    goKeepIncludedAllocation,
    handleUpsellSelectOpenChange,
    goLockInDataAndFrequencyUpsell,
    showUpgradeToCard,
    computeOriginalPriceCompact,
    formatUsdCompact,
    frequencyDisplayLabel,
    frequencySuffixFromKey,
    parseCurrencyToNumber,
    upgradeCardTitle,
  };
}
